import React, { createContext, useState, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchWithAuth } from "../services/apiService";
import { getApiUrl } from "../services/api";
import { tokenStore } from "../services/tokenStore";
import { fetchNotifications, handleReceiveNotification } from "../store/slices/notificationsSlice";

export const NotificationsContext = createContext();

const BASE_URL = "api/UserNotifications";
const UNREAD_COUNT_TTL = 60 * 1000;

export const NotificationsProvider = ({ children }) => {
    const [unreadCount, setUnreadCount] = useState(0);
    const isLoggedIn = useSelector((s) => s.auth.isLoggedIn);
    const dispatch = useDispatch();

    const loadingRef = useRef(false);
    const lastFetchedRef = useRef(0);
    const stopSignalRRef = useRef(null);

    const fetchUnreadCount = useCallback(async ({ force = false } = {}) => {
        if (!tokenStore.get()) return;

        const now = Date.now();

        if (!force && loadingRef.current) {
            return;
        }

        if (!force && now - lastFetchedRef.current < UNREAD_COUNT_TTL) {
            return;
        }

        loadingRef.current = true;

        try {
            const data = await fetchWithAuth(getApiUrl(`${BASE_URL}/unread-count`), {
                method: "GET",
            });

            if (typeof data?.unreadCount === "number") {
                setUnreadCount(data.unreadCount);
                lastFetchedRef.current = Date.now();
            }
        } catch {
        } finally {
            loadingRef.current = false;
        }
    }, []);

    useEffect(() => {
        if (!isLoggedIn || !tokenStore.get()) {
            setUnreadCount(0);

            try {
                stopSignalRRef.current?.();
            } catch {
            }

            stopSignalRRef.current = null;
            return;
        }

        fetchUnreadCount();

        let cancelled = false;

        if (!stopSignalRRef.current) {
            import("../services/signalrService")
                .then(({ startSignalRConnection }) =>
                    startSignalRConnection(
                        () => tokenStore.get(),
                        (notification) => {
                            dispatch(handleReceiveNotification(notification));
                            fetchUnreadCount({ force: true });
                        },
                        () => {
                            dispatch(fetchNotifications(1));
                            fetchUnreadCount({ force: true });
                        }
                    )
                )
                .then((stop) => {
                    if (cancelled) {
                        stop?.();
                        return;
                    }

                    stopSignalRRef.current = stop;
                })
                .catch(() => {});
        }

        return () => {
            cancelled = true;

            try {
                stopSignalRRef.current?.();
            } catch {
            }

            stopSignalRRef.current = null;
        };
    }, [isLoggedIn, fetchUnreadCount, dispatch]);

    const markAllAsRead = async (notificationIds) => {
        if (!tokenStore.get()) return;

        try {
            await fetchWithAuth(getApiUrl(`${BASE_URL}/read`), {
                method: "PATCH",
                body: JSON.stringify({ notificationIds }),
            });

            if (Array.isArray(notificationIds) && notificationIds.length > 0) {
                setUnreadCount((prev) => Math.max(prev - notificationIds.length, 0));
            } else {
                setUnreadCount(0);
            }
        } catch {
        }
    };

    const deleteNotification = async (notificationId) => {
        if (!tokenStore.get()) return;

        try {
            await fetchWithAuth(getApiUrl(`${BASE_URL}/${notificationId}`), {
                method: "DELETE",
            });

            await fetchUnreadCount({ force: true });
        } catch {
        }
    };

    return (
        <NotificationsContext.Provider
            value={{
                unreadCount,
                fetchUnreadCount,
                markAllAsRead,
                deleteNotification,
            }}
        >
            {children}
        </NotificationsContext.Provider>
    );
};
